import { useCallback, useEffect, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { useLoadingStore } from '@hsp/react-component-library';
import { isDefined, isNotDefined, isNotEmpty } from '@hsp/utilities';

import { useAssignMailCode, useSaveStoreSelector } from 'pages/audience/shared/save/hooks';
import { SaveForm } from 'types/pages/audience-builder/shared/save';

export const useMailCodeConfiguration = () => {
  const data = useSaveStoreSelector(({ data }) => data);
  const saveAsMaster = useSaveStoreSelector(({ saveAsMaster }) => saveAsMaster);
  const saveAsPreBuilt = useSaveStoreSelector(({ saveAsPreBuilt }) => saveAsPreBuilt);
  const saveTo = useSaveStoreSelector(({ saveTo }) => saveTo);

  const [isAssignMailCodeEnabled, setIsAssignMailCodeEnabled] = useState<boolean>(false);

  const { mutateAsync } = useAssignMailCode();
  const {
    formState: { defaultValues, errors },
    getValues,
    register,
    setValue,
    watch,
  } = useFormContext<SaveForm>();
  const setLoading = useLoadingStore(({ setIsLoading }) => setIsLoading);

  const { ref, ...props } = register('mailCode');
  const [mailCode, orderNumber] = watch(['mailCode', 'orderNumber']);

  const handleAssignMailCode = useCallback(() => {
    const values = getValues();

    if (isNotDefined(values)) {
      return;
    }

    const { id, type } = values;
    let orderNumber: string | null | undefined = null;
    let shopId: string | null | undefined = null;

    if (type === 'basic') {
      ({ orderNumber, shopId } = values);
    }

    setLoading(true);

    mutateAsync({ id, orderNumber: orderNumber ?? null, shopId: saveTo ? null : shopId })
      .then(({ dataContainer, success }) => {
        if (success && isDefined(dataContainer)) {
          const { mailCode, sequenceNumber } = dataContainer;

          setValue('mailCode', mailCode, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
          setValue('sequenceNumber', sequenceNumber, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        }
      })
      .finally(() => setLoading(false));
  }, [getValues, mutateAsync, saveTo, setLoading, setValue]);

  useEffect(() => {
    if (isNotEmpty(orderNumber)) {
      const formData = defaultValues as SaveForm;
      const { type } = formData;

      if (type === 'basic') {
        const { mailCode: defaultMailCode, orderNumber: defaultOrderNumber } = formData;

        setIsAssignMailCodeEnabled(
          orderNumber !== defaultOrderNumber ||
            mailCode !== defaultMailCode ||
            (isDefined(formData.id) && isNotDefined(formData.shopId))
        );
      }

      return;
    }

    setIsAssignMailCodeEnabled(false);
  }, [data, defaultValues, mailCode, orderNumber]);

  useEffect(() => {
    if (saveAsMaster || saveAsPreBuilt || saveTo) {
      setValue('mailCode', '');
    }
  }, [saveAsMaster, saveAsPreBuilt, saveTo, setValue]);

  return { errors, handleAssignMailCode, isAssignMailCodeEnabled, props, ref };
};
